import React, { forwardRef, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import { useGetPainelOptionsQuery, useGetEntidadesQuery } from 'services/api/windx';
import {
  Entidade,
  EntidadeSimples,
  EntidadesListParams,
  Subdivisao,
  Usina,
  WindxTela,
} from 'helper/windx';
import {
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  selectWindxDataSimulacao,
  setIdSubdivisaoEspacial,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import './index.scss';

interface SubdivisaoUnificadaProps {
  tela: Extract<WindxTela, 'sh' | 'sm'>;
  onAbrirSeletor: () => void;
  // Comparativo SM passa `true`: "0 selecionadas" vira "Todas as N entidades"
  // no rotulo da Tag (em vez de tratar como estado inicial).
  permiteSelecaoVazia?: boolean;
  // 'dropdown' (padrao, desktop): popover do react-bootstrap preso ao chip.
  // 'sheet' (mobile): BottomSheet cheio, com as opcoes em OptionRow.
  apresentacaoEscolha?: 'dropdown' | 'sheet';
}

interface ChipToggleProps {
  rotulo: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  id?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-expanded'?: boolean;
  'aria-labelledby'?: string;
}

const ChipToggle = forwardRef<HTMLButtonElement, ChipToggleProps>(
  (
    {
      rotulo,
      onClick,
      onMouseDown,
      onKeyDown,
      id,
      'aria-haspopup': ariaHasPopup,
      'aria-expanded': ariaExpanded,
      'aria-labelledby': ariaLabelledBy,
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      id={id}
      className="windx-chip-toggle windx-subdivisao-unificada-toggle"
      onClick={onClick}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-labelledby={ariaLabelledBy}
      data-testid="windx-subdivisao-unificada-toggle"
    >
      <span className="windx-subdivisao-unificada-toggle__rotulo">{rotulo}</span>
      <span className="windx-chip-toggle__caret" aria-hidden="true" />
    </button>
  ),
);
ChipToggle.displayName = 'SubdivisaoUnificadaChipToggle';

/**
 * Caixa unica que substitui o par `Subdivision` + `SeletorEntidadesResumo`
 * no `CabecalhoGrafico` (FE-LAYOUT-011 parte 2). Inspirado em
 * `Flux/Automatic/FilterSelector` + `ActiveFilters` do Flux Daily.
 *
 * Estados visuais:
 *   - **A. Sem subdivisao** → chip cinza "Subdivisao espacial" + caret;
 *     click abre a escolha das 4 opcoes (Subsistemas, Regioes EPE,
 *     Conjuntos, Usinas) — dropdown do react-bootstrap por padrao, ou
 *     `BottomSheet` quando `apresentacaoEscolha="sheet"` (mobile).
 *     Selecionar uma despacha `setIdSubdivisaoEspacial(id)` + reset de
 *     `idsEntidades` e chama `onAbrirSeletor()` para o modal apropriado.
 *   - **B. Subdivisao escolhida + 0 entidades** → mesmo chip+escolha, mas
 *     rotulo mostra o label da subdivisao atual; selecionar a mesma opcao
 *     reabre o modal, outra opcao troca subdivisao + reset + reabre.
 *   - **C. Entidades selecionadas** (ou Comparativo SM com 0 = "todas") →
 *     `<Tag>` DS com label combinado e icone × para limpar tudo (volta a
 *     estado A). Click no corpo da Tag reabre o modal.
 *
 * O modal continua sendo o `<SeletorEntidades>` mantido pelo
 * `CabecalhoGrafico` (via prop `onAbrirSeletor`). Componente nao gerencia
 * o estado de "show" — so dispara abertura.
 */
export default function SubdivisaoUnificada({
  tela,
  onAbrirSeletor,
  permiteSelecaoVazia = false,
  apresentacaoEscolha = 'dropdown',
}: SubdivisaoUnificadaProps) {
  const dispatch = useDispatch();
  const { data: opts } = useGetPainelOptionsQuery(tela);
  const idSubdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);
  const [pickerAberto, setPickerAberto] = useState(false);

  // Busca a entidade selecionada quando ha exatamente uma — para exibir
  // nome amigavel no Tag (ex.: "Subsistema — SIN"). Skip quando ha 0 ou >1.
  const queryArg = useMemo<EntidadesListParams | undefined>(() => {
    if (idSubdivisao === undefined) return undefined;
    const arg: EntidadesListParams = { subdivisao: idSubdivisao };
    if (telaAtiva === 'sh' || telaAtiva === 'sm') {
      arg.tela = telaAtiva as WindxTela;
    }
    // WINDX-034: dataSimulacao eh fonte unica do modo de operacao.
    if (dataSimulacao) {
      arg.data_simulacao = dataSimulacao;
    }
    return arg;
  }, [idSubdivisao, telaAtiva, dataSimulacao]);

  // O `skip` abaixo cumpre o que o comentario acima ja prometia: a lista so e
  // consumida por `rotularSelecao` quando ha EXATAMENTE uma entidade
  // selecionada (ver `idsEntidades.length === 1`). Nos demais estados a
  // resposta era buscada e descartada — e em subdivisao=4 isso significava
  // pedir as 1.052 usinas so para montar o texto de um chip.
  const { data: entidades } = useGetEntidadesQuery(queryArg as EntidadesListParams, {
    skip: !queryArg || idsEntidades.length !== 1,
  });

  const subdivisoes: Subdivisao[] = opts?.subdivisoes_espaciais ?? [];
  const subdivisaoAtual = subdivisoes.find((s) => s.id === idSubdivisao);

  const handleEscolherSubdivisao = (idNovo: number) => {
    if (idNovo === idSubdivisao) {
      onAbrirSeletor();
      return;
    }
    dispatch(setIdSubdivisaoEspacial(idNovo));
    dispatch(setIdsEntidades([]));
    onAbrirSeletor();
  };

  const handleLimpar = () => {
    dispatch(setIdSubdivisaoEspacial(undefined));
    dispatch(setIdsEntidades([]));
  };

  const semSelecaoEhTodas = idsEntidades.length === 0
    && permiteSelecaoVazia
    && telaAtiva === 'sm';

  // Estado C: renderiza Tag pill quando ha entidades selecionadas OU quando
  // "0 marcadas = todas" se aplica (Comparativo SM).
  const renderTag = subdivisaoAtual && (idsEntidades.length > 0 || semSelecaoEhTodas);

  if (renderTag) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const rotuloTag = rotularSelecao({
      subdivisaoLabel: subdivisaoAtual.label,
      idsEntidades,
      entidades: entidades as Entidade[] | undefined,
      semSelecaoEhTodas,
    });
    // Bug do PO 2026-05-18 #1: a versao anterior usava o <Tag> DS com
    // `onClick={handleLimpar}` no icone close DENTRO de um <button>
    // wrapper com `onClick={onAbrirSeletor}`. O click no x propagava
    // para o button pai (event bubbling) e disparava ambos os handlers,
    // limpando os filtros E abrindo o modal — que entao aparecia vazio
    // porque `idSubdivisaoEspacial` ja era undefined. Agora a Tag eh
    // composta por dois <button> IRMAOS dentro de um container `<div>`
    // sem onClick: clicks ficam isolados por design (nao ha nesting).
    return (
      <div
        className="windx-subdivisao-unificada windx-subdivisao-unificada--ativa"
        data-testid="windx-subdivisao-unificada-tag"
      >
        <div className="windx-subdivisao-unificada__tag-pill">
          <button
            type="button"
            className="windx-subdivisao-unificada__tag-body"
            onClick={onAbrirSeletor}
            aria-label={`Editar selecao (${rotuloTag})`}
          >
            {rotuloTag}
          </button>
          <button
            type="button"
            className="windx-subdivisao-unificada__tag-close"
            onClick={handleLimpar}
            aria-label={`Limpar selecao (${rotuloTag})`}
            data-testid="windx-subdivisao-unificada-tag-close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    );
  }

  // Estado A ou B: chip + escolha de subdivisao (dropdown no desktop, sheet no mobile).
  const rotuloChip = subdivisaoAtual ? subdivisaoAtual.label : 'Subdivisão espacial';

  if (apresentacaoEscolha === 'sheet') {
    const escolher = (id: number) => {
      setPickerAberto(false);
      handleEscolherSubdivisao(id);
    };
    return (
      <div className="windx-subdivisao-unificada">
        <ChipToggle
          rotulo={rotuloChip}
          onClick={() => setPickerAberto(true)}
          aria-haspopup="dialog"
          aria-expanded={pickerAberto}
        />
        <BottomSheet
          open={pickerAberto}
          dismiss={() => setPickerAberto(false)}
          title="Subdivisão espacial"
          className="windx-subdivisao-unificada-sheet"
        >
          {subdivisoes.map((s) => (
            <OptionRow
              key={s.id}
              label={s.label}
              selected={s.id === idSubdivisao}
              onSelect={() => escolher(s.id)}
            />
          ))}
        </BottomSheet>
      </div>
    );
  }

  return (
    <div className="windx-subdivisao-unificada">
      <Dropdown>
        <Dropdown.Toggle as={ChipToggle} rotulo={rotuloChip} />
        <Dropdown.Menu className="windx-subdivisao-unificada-menu">
          {subdivisoes.map((s) => (
            <Dropdown.Item
              key={s.id}
              onClick={() => handleEscolherSubdivisao(s.id)}
              className={`windx-subdivisao-unificada-item ${
                s.id === idSubdivisao ? 'active' : ''
              }`}
            >
              {s.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface RotularArgs {
  subdivisaoLabel: string;
  idsEntidades: number[];
  entidades: Entidade[] | undefined;
  semSelecaoEhTodas: boolean;
}

function rotularSelecao({
  subdivisaoLabel,
  idsEntidades,
  entidades,
  semSelecaoEhTodas,
}: RotularArgs): string {
  if (semSelecaoEhTodas) {
    return `${subdivisaoLabel} — Todas`;
  }
  if (idsEntidades.length === 1 && entidades?.length) {
    const escolhida = entidades.find((e) => e.id === idsEntidades[0]);
    if (escolhida) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const nomeAmigavel = nomeDaEntidade(escolhida);
      return `${subdivisaoLabel} — ${nomeAmigavel}`;
    }
  }
  return `${subdivisaoLabel} — ${idsEntidades.length} selecionada${
    idsEntidades.length === 1 ? '' : 's'
  }`;
}

function nomeDaEntidade(e: Entidade): string {
  if ('codigo' in e) {
    return (e as Usina).nome || (e as Usina).codigo;
  }
  const simples = e as EntidadeSimples;
  return simples.label || simples.nome || `#${simples.id}`;
}
